import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from "@/components/ErrorBoundary";

// Critical pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";

const PageLoader = () => (
  <div className="min-h-screen bg-background">
    <div className="h-16 bg-primary" />
    <div className="h-80 bg-muted animate-pulse" />
    <div className="container mx-auto px-4 py-12 space-y-6">
      <Skeleton className="h-8 w-2/3 mx-auto" />
      <Skeleton className="h-4 w-1/2 mx-auto" />
      <div className="grid md:grid-cols-3 gap-6 mt-8">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

// Public pages
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogCategories = lazy(() => import("./pages/BlogCategories"));
const BlogTag = lazy(() => import("./pages/BlogTag"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ThankYou = lazy(() => import("./pages/ThankYou"));

// New Nail Point Records pages
const Artists = lazy(() => import("./pages/Artists"));
const ArtistProfile = lazy(() => import("./pages/ArtistProfile"));
const Services = lazy(() => import("./pages/Services"));
const Mastering = lazy(() => import("./pages/Mastering"));
const Apply = lazy(() => import("./pages/Apply"));
const Theology = lazy(() => import("./pages/Theology"));

// Portal pages
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const ResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const Invoices = lazy(() => import("./pages/portal/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/portal/InvoiceDetail"));
const Releases = lazy(() => import("./pages/portal/Releases"));
const ReleaseDetail = lazy(() => import("./pages/portal/ReleaseDetail"));
const MasteringOrders = lazy(() => import("./pages/portal/MasteringOrders"));
const MasteringOrderDetail = lazy(() => import("./pages/portal/MasteringOrderDetail"));
const Contracts = lazy(() => import("./pages/portal/Contracts"));
const PortalFiles = lazy(() => import("./pages/portal/PortalFiles"));
const Support = lazy(() => import("./pages/portal/Support"));
const Settings = lazy(() => import("./pages/portal/Settings"));
const MasterCalendar = lazy(() => import("./pages/portal/MasterCalendar"));
const AcceptInvitation = lazy(() => import("./pages/portal/AcceptInvitation"));
const PaymentSuccess = lazy(() => import("./pages/portal/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/portal/PaymentCanceled"));

// Admin pages
const UserManagement = lazy(() => import("./pages/portal/admin/UserManagement"));
const OrgManagement = lazy(() => import("./pages/portal/admin/OrgManagement"));
const AdminSettings = lazy(() => import("./pages/portal/admin/AdminSettings"));
const BookingSettings = lazy(() => import("./pages/portal/admin/BookingSettings"));
const BookingManagement = lazy(() => import("./pages/portal/admin/BookingManagement"));
const OrgModules = lazy(() => import("./pages/portal/admin/OrgModules"));
const InvoiceEntry = lazy(() => import("./pages/portal/admin/InvoiceEntry"));
const ArtistApplications = lazy(() => import("./pages/portal/admin/ArtistApplications"));
const AdminMastering = lazy(() => import("./pages/portal/admin/AdminMastering"));

import { PortalLayout } from "./components/portal/PortalLayout";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/artists" element={<Artists />} />
                <Route path="/artists/:slug" element={<ArtistProfile />} />
                <Route path="/services" element={<Services />} />
                <Route path="/mastering" element={<Mastering />} />
                <Route path="/apply" element={<Apply />} />
                <Route path="/theology" element={<Theology />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/category/:categorySlug" element={<Blog />} />
                <Route path="/blog/categories" element={<BlogCategories />} />
                <Route path="/blog/tag/:tagSlug" element={<BlogTag />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/thank-you" element={<ThankYou />} />

                {/* Portal Routes */}
                <Route path="/portal/login" element={<PortalLogin />} />
                <Route path="/portal/reset-password" element={<ResetPassword />} />
                <Route path="/portal/accept-invite" element={<AcceptInvitation />} />
                <Route path="/portal/payment-success" element={<PaymentSuccess />} />
                <Route path="/portal/payment-canceled" element={<PaymentCanceled />} />
                <Route path="/portal" element={<PortalLayout />}>
                  <Route index element={<PortalDashboard />} />
                  <Route path="invoices" element={<Invoices />} />
                  <Route path="invoices/:id" element={<InvoiceDetail />} />
                  <Route path="releases" element={<Releases />} />
                  <Route path="releases/:id" element={<ReleaseDetail />} />
                  <Route path="mastering" element={<MasteringOrders />} />
                  <Route path="mastering/:id" element={<MasteringOrderDetail />} />
                  <Route path="contracts" element={<Contracts />} />
                  <Route path="files" element={<PortalFiles />} />
                  <Route path="support" element={<Support />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="calendar" element={<MasterCalendar />} />
                  {/* Admin Routes */}
                  <Route path="admin/settings" element={<AdminSettings />} />
                  <Route path="admin/users" element={<UserManagement />} />
                  <Route path="admin/organizations" element={<OrgManagement />} />
                  <Route path="admin/modules" element={<OrgModules />} />
                  <Route path="admin/invoices/new" element={<InvoiceEntry />} />
                  <Route path="admin/applications" element={<ArtistApplications />} />
                  <Route path="admin/mastering" element={<AdminMastering />} />
                  <Route path="admin/booking-settings" element={<BookingSettings />} />
                  <Route path="admin/bookings" element={<BookingManagement />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
